import React from 'react'
import styled from 'styled-components'
import { useKey } from 'react-use'
import { useRouteMatch } from 'react-router-dom'

import { PersonAvatar } from '@sketch/components'

import CommentDate from '../CommentDate'
import CommentAuthor from '../CommentAuthor'

import { Wrapper, CommentInput, HeaderWrapper } from './EditCommentInput.styles'
import { CommentUserFragment, useEditCommentMutation } from '@sketch/gql-types'
import { useIsAnnotationPreview } from '../../context'
import { routes, usePreviewPwd } from '../../../routes'

interface EditCommentInputProps {
  className?: string
  'data-first-comment'?: boolean
  identifier: string
  shareIdentifier: string
  createdAt: string
  user: CommentUserFragment
  comment: string
  onCancel: () => void
}

const EditCommentInput = (props: EditCommentInputProps) => {
  const {
    identifier,
    shareIdentifier,
    user,
    createdAt,
    comment,
    onCancel,
    ...divProps
  } = props
  const isPreview = useIsAnnotationPreview()
  const previewRouteMatch = useRouteMatch<{ previewID?: string }>(
    routes.PREVIEW_VIEW.template()
  )
  const previewIdentifier = previewRouteMatch?.params.previewID
  const previewPassword = usePreviewPwd(previewIdentifier || '')
  const mentionableUsersSource =
    isPreview && previewIdentifier
      ? { previewIdentifier, previewPassword }
      : undefined

  const [updateComment] = useEditCommentMutation({
    onError: 'show-toast',
    optimisticResponse: ({ body, identifier }) => ({
      __typename: 'RootMutationType',
      editComment: {
        __typename: 'EditCommentResponse',
        comment: {
          __typename: 'Comment',
          body,
          identifier,
          isEdited: true,
        },
      },
    }),
  })

  // Close the edit-mode when the "escape" key has been touched
  useKey('Escape', () => {
    onCancel()
  })

  return (
    <Wrapper {...divProps}>
      <HeaderWrapper>
        <PersonAvatar
          flavour="image"
          size="17px"
          src={user.avatar!.small!}
          name={user.name!}
        />
        <CommentAuthor placement="bottom-end">{user.name}</CommentAuthor>
        <div>
          <CommentDate date={createdAt} />
        </div>
      </HeaderWrapper>
      <CommentInput
        shareIdentifier={shareIdentifier}
        onCommentSubmit={body => {
          updateComment({ variables: { body, identifier } })
          onCancel()
        }}
        onCancel={onCancel}
        comment={comment}
        isEditing
        autoFocus
        mentionableUsersSource={mentionableUsersSource}
      />
    </Wrapper>
  )
}

export default styled(EditCommentInput)``
